import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { items, shape, required, fromModel, when } from '@totalsoft/pure-validations';
import { isMobilePhoneByCountry, isFullPersonName, hasMultipleEmails } from 'utils/pure-validation/primitiveValidators';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { loadPositions, loadPartnerDetails, saveContactPersons, loadContactPersons, loadSegregatedPersons } from '../actionCreators';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Prompt } from 'react-router-dom';
import ContactMail from '@mui/icons-material/ContactMail';
import { withTranslation } from 'react-i18next';
import OfferPartnerContactPersonsSummary from './OfferPartnerContactPersonsSummary';
import OfferPartnerContactPersonsListComponent from './OfferPartnerContactPersonsListComponent';
import { withParams } from 'providers/ParamsConsumer';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import WrappedFakeText from 'components/common/WrappedFakeText';

const contactPersonsValidator = countryCode => {
  return items(
    fromModel(item =>
      shape({
        personName: isFullPersonName,
        email: hasMultipleEmails |> when(Boolean(item.email)),
        phone: isMobilePhoneByCountry(countryCode),
        positionId: required
      })
    )
  );
};

export const OfferPartnerContactPersonsContainer = ({
  t,
  partnerId,
  documentId,
  siteId,
  openMasterPartner,
  openDocumentPartner,
  masterPartnerApp,
  clientContactinfoWrite,
  params,
  isAssociatedPartner
}) => {
  const dispatch = useDispatch();

  const loaded = useSelector(
    state =>
      state.currentOffer.clientContactPersonsData[partnerId] ? state.currentOffer.clientContactPersonsData[partnerId].loaded : false,
    shallowEqual
  );

  const contactPersons = useSelector(
    state => (state.entities.contactPersons ? state.entities.contactPersons[partnerId] : []),
    shallowEqual
  );
  const [contactPersonsCardExpanded, setContactPersonsCardExpanded] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (contactPersonsCardExpanded && !loaded && partnerId) {
      dispatch(loadPositions());
      dispatch(loadContactPersons(documentId, siteId, partnerId));
    }
  }, [contactPersonsCardExpanded, loaded, dispatch, documentId, siteId, partnerId]);

  useEffect(() => {
    if (loaded) {
      setLocalContactPersonsList(contactPersons);
    }
  }, [loaded, contactPersons]);

  const positions = useSelector(
    state => (state.entities.positions ? Object.keys(state.entities.positions).map(i => state.entities.positions[i]) : []),
    shallowEqual
  );

  const [localContactPersons, setLocalContactPersonsList] = useState([]);

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();

  const validatorContactPersons = useMemo(() => contactPersonsValidator(params.countryCode), [params.countryCode]);
  const [contactPersonsValidation, validateContactPersons, resetDirtyInfo] = useDirtyFieldValidation(validatorContactPersons, dirtyInfo);

  const [saveContactPersonDisabled, setSaveContactPersonDisabled] = useState(true);
  const [validDefaultContactPerson, setValidDefaultContactPerson] = useState(true);
  const [saving, setSaving] = useState(false);

  const onContactPersonsCardExpand = (_, expanded) => {
    setContactPersonsCardExpanded(!expanded);
  };

  const onContactPersonClear = idx => {
    setDirtyInfo(`${idx}`);
    let newContactPersons = localContactPersons;
    let contactPersonList = Object.assign([], {
      ...newContactPersons,
      [idx]: {
        ...newContactPersons[idx],
        uniqueID: null,
        personName: null,
        email: null,
        phone: null,
        personId: null,
        positionId: null,
        isDefaultContact: false,
        isESignature: false,
        contactInfoId: 0,
        isLegalRepresentative: false
      }
    });
    setLocalContactPersonsList(contactPersonList);
    setSaveContactPersonDisabled(false);
    setIsDirty(true);
  };
  const adminPositionId = useMemo(() => {
    return positions.find(position => position.positionCode === params.administratorPartnerPositionCode)?.positionId;
  }, [params.administratorPartnerPositionCode, positions]);

  const onContactPersonPropertyChange =
    idx =>
    (prop1, prop2, callbackFn = () => {}) =>
    (value1, value2) => {
      setDirtyInfo(`${idx}.${prop1}`);
      if (value2) {
        callbackFn(value2, idx);
      } else {
        setLocalContactPersonsList(state =>
          Object.assign([state], {
            ...state,
            [idx]: {
              ...state[idx],
              [prop1]: value1,
              ...(prop2 && { [prop2]: value2 })
            }
          })
        );
      }

      if (prop1 === 'positionId' && value1 === adminPositionId) {
        setLocalContactPersonsList(state =>
          Object.assign([state], {
            ...state,
            [idx]: {
              ...state[idx],
              isLegalRepresentative: true
            }
          })
        );
      }

      setSaveContactPersonDisabled(false);
      setValidDefaultContactPerson(true);
      setIsDirty(true);
    };

  const addContactPerson = () => {
    let contactPersonsList = [...localContactPersons, {}];
    if (localContactPersons.length === 0) contactPersonsList = [...localContactPersons, { isDefaultContact: true }];

    setLocalContactPersonsList(contactPersonsList);
    setSaveContactPersonDisabled(false);
    setIsDirty(true);
    setValidDefaultContactPerson(true);
  };

  const deleteContactPerson = item => {
    setDirtyInfo(`${localContactPersons.indexOf(item)}`);
    let newContactPersonsList = localContactPersons.filter(l => localContactPersons.indexOf(l) !== localContactPersons.indexOf(item));
    setLocalContactPersonsList(newContactPersonsList);
    setSaveContactPersonDisabled(false);
    setIsDirty(true);
  };

  const loadPartnerDetailList = useCallback(
    (uniqueID, idx) => {
      if (!uniqueID) return Promise.resolve([]);

      dispatch(loadPartnerDetails(uniqueID)).then(data => {
        setLocalContactPersonsList(state =>
          Object.assign([state], {
            ...state,
            [idx]: {
              ...state[idx],
              uniqueID: data?.uniqueID ?? uniqueID,
              personName: data?.partnerName ?? state[idx].personName,
              email: data?.e_Mail ?? state[idx].email,
              phone: data?.phoneNumber ?? state[idx].phone,
              phoneCountryCode: data?.phoneCountryCode ?? params.countryCode,
              personId: data?.partnerId
            }
          })
        );
      });
    },
    [dispatch, params]
  );

  const loadPersonList = search => {
    if (!search) return Promise.resolve([]);

    return dispatch(loadSegregatedPersons(search)).then(data => {
      return data.map(item => {
        return { id: item.id, uniqueID: item.uniqueID };
      });
    });
  };

  const saveContactPersonList = (partnerId, localContactPersons) => {
    if (!validateContactPersons(localContactPersons)) return;

    const defaultContactPersons = getDefaultContactPersons(localContactPersons);
    if (defaultContactPersons.length > 1) setValidDefaultContactPerson(false);
    else {
      setSaving(true);
      dispatch(
        saveContactPersons(documentId, siteId, partnerId, localContactPersons, () => {
          setIsDirty(false);
          resetDirtyInfo();
        })
      ).then(() => setSaving(false));
      setSaveContactPersonDisabled(true);
    }
  };

  const getDefaultContactPersons = localContactPersons => {
    return localContactPersons.filter(contactPerson => contactPerson.isDefaultContact === true);
  };

  useEffect(() => {
    validateContactPersons(localContactPersons, dirtyInfo);
  }, [localContactPersons, dirtyInfo, validateContactPersons]);

  return (
    <CollapseOrBlankCard
      id={'offerPartnerContactPersonsCard'}
      icon={ContactMail}
      subheader={<OfferPartnerContactPersonsSummary documentId={documentId} siteId={siteId} partnerId={partnerId} />}
      hideSubheaderOnExpand
      title={t('OfferEdit.Client.ContactPersons.Title')}
      expanded={contactPersonsCardExpanded}
      onToggle={onContactPersonsCardExpand}
      canExpand={partnerId != null}
    >
      <>
        <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
        {!loaded && contactPersonsCardExpanded ? (
          <WrappedFakeText />
        ) : (
          <OfferPartnerContactPersonsListComponent
            contactPersonList={localContactPersons}
            validation={contactPersonsValidation}
            positions={positions}
            addContactPerson={addContactPerson}
            onPropertyChange={onContactPersonPropertyChange}
            openMasterPartner={openMasterPartner}
            openDocumentPartner={openDocumentPartner}
            masterPartnerApp={masterPartnerApp}
            saveContactPersons={saveContactPersonList}
            saving={saving}
            deleteContactPerson={deleteContactPerson}
            loadPersons={loadPersonList}
            partnerId={partnerId}
            saveDisabled={saveContactPersonDisabled}
            validDefaultContactPerson={validDefaultContactPerson}
            loadPartnerDetails={loadPartnerDetailList}
            clientContactinfoWrite={clientContactinfoWrite}
            onContactPersonClear={onContactPersonClear}
            showESignatureCheckboxFromContactPersonsCard={params.showESignatureCheckboxFromContactPersonsCard}
            isAssociatedPartner={isAssociatedPartner}
          />
        )}
      </>
    </CollapseOrBlankCard>
  );
};

OfferPartnerContactPersonsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  partnerId: PropTypes.number.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  openMasterPartner: PropTypes.func.isRequired,
  openDocumentPartner: PropTypes.func.isRequired,
  masterPartnerApp: PropTypes.bool,
  clientContactinfoWrite: PropTypes.bool.isRequired,
  params: PropTypes.object,
  isAssociatedPartner: PropTypes.bool.isRequired
};

export default withTranslation()(withParams(OfferPartnerContactPersonsContainer));
